import { forwardRef, useRef, useState } from 'react'

import { type IComment } from 'types/interfaces/ITypes'

import Comment from './Comment'

interface ICommentEntity {
    entityId: string
    parentId: string
    owner?: string
    ownerId: string
    isReply: boolean
}

interface Props {
    data: IComment
    refetchComments: () => void
    selectedEntity: ICommentEntity | null
    isCommentReply: boolean
    setIsCommentReply: (value: boolean) => void
    setSelectedEntity: (commentEntity: ICommentEntity | null) => void
    setInitialCommentLiked: (commentId: string) => void
    deleteComment: (
        commentId: string,
        parentId: string,
        isReply: boolean
    ) => void
    isMyPost: boolean
    optionsActive: boolean
    setOptionsActive: React.Dispatch<React.SetStateAction<boolean>>
    startReply: (params: {
        entityId: string
        parentId: string
        ownerId: string
        owner?: string
        isReply: boolean
    }) => void
}

const CommentBlock = forwardRef<HTMLInputElement, Props>(
    (
        {
            data,
            refetchComments,
            selectedEntity,
            isCommentReply,
            setIsCommentReply,
            setSelectedEntity,
            setInitialCommentLiked,
            deleteComment,
            isMyPost,
            optionsActive,
            setOptionsActive,
            startReply
        },
        ref
    ) => {
        const [repliesToggled, setRepliesToggled] = useState<boolean>(false)
        const replyRef = useRef<HTMLDivElement>(null)

        const showReplyOptionsActive = () => {
            if (optionsActive && selectedEntity?.isReply) return true
            return false
        }

        const shouldShowReplyOptions = showReplyOptionsActive()
        const replies: IComment[] = Array.isArray(data.comments) ? data.comments : []

        return (
            <>
                <Comment
                    ref={ref}
                    data={data}
                    refetchComments={refetchComments}
                    repliesToggled={repliesToggled}
                    toggleReplies={() => setRepliesToggled(!repliesToggled)}
                    isReply={!!data.parent_id}
                    commentSelected={isCommentReply && selectedEntity?.entityId === data.id}
                    selectedEntity={selectedEntity}
                    isCommentReply={isCommentReply}
                    setIsCommentReply={setIsCommentReply}
                    setSelectedEntity={setSelectedEntity}
                    setInitialCommentLiked={setInitialCommentLiked}
                    deleteComment={() => {
                        const commentId = data.id
                        const parentId = data.parent_id ?? data.id

                        deleteComment(commentId, parentId, !!data.parent_id)
                    }}
                    isMyPost={isMyPost}
                    optionsActive={optionsActive}
                    setOptionsActive={setOptionsActive}
                    startReply={startReply}
                />

                <div
                    ref={replyRef}
                    style={{
                        height: repliesToggled ? 'auto' : 0
                    }}
                    className={`comment__replies ${repliesToggled ? 'comment__replies--active' : ''}`}
                >
                    {repliesToggled &&
                        replies.length > 0 &&
                        replies.map((reply: IComment, index: number) => (
                            <div key={reply.id ?? index}>
                                <CommentBlock
                                    ref={ref}
                                    data={reply}
                                    refetchComments={refetchComments}
                                    selectedEntity={selectedEntity}
                                    isCommentReply={isCommentReply}
                                    setIsCommentReply={setIsCommentReply}
                                    setSelectedEntity={setSelectedEntity}
                                    setInitialCommentLiked={setInitialCommentLiked}
                                    deleteComment={deleteComment}
                                    isMyPost={isMyPost}
                                    optionsActive={shouldShowReplyOptions}
                                    setOptionsActive={setOptionsActive}
                                    startReply={startReply}
                                />
                            </div>
                        ))}
                </div>
            </>
        )
    }
)

CommentBlock.displayName = 'CommentBlock'
export default CommentBlock